import { Avatar } from "../../../../../../../../../components/Avatar/Avatar";
import { Tooltip } from "../../../../../../../../../components/Tooltip/Tooltip";
import EmailIcon from "../../../../../../../../../assets/svg/v1/email-icon";
import { customToast } from "../../../../../../../../../utils/custom-toast/custom-toast";
import { TicketData } from "../../../../../utils/types";
import { getHelpdeskLabels } from "../../../../../../../../../utils/constants/mappings";
import { useSelector } from "react-redux";
import { RootState } from "../../../../../../../../../redux/store";

interface TicketDetailsContentProps {
  ticketData?: TicketData;
  isLoading?: boolean;
}

const TicketDetailsContent = ({
  ticketData,
  isLoading,
}: TicketDetailsContentProps) => {
  const name = ticketData?.name || "";
  const email = ticketData?.email || "";
  const region = useSelector(
    (state: RootState) => state?.auth?.client?.region
  );
  const helpdeskLabels = getHelpdeskLabels(region ?? "UK");
  const handleCopyEmail = () => {
    if (!email) return;
    navigator?.clipboard?.writeText(email);
    customToast("Email copied to clipboard", "success");
  };

  if (isLoading && !ticketData) {
    return (
      <>
        <div className="flex items-center h-[50px] px-4 border-b border-border-gray dark:border-border-gray-dk bg-pure-white dark:bg-pure-white-dk">
          <div className="h-4 w-28 bg-gray-200 dark:bg-gray-700 rounded animate-pulse" />
        </div>
        <div className="flex-1 p-4">
          <div className="border border-border-gray dark:border-border-gray-dk rounded-lg p-4">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-8 h-8 rounded-full bg-gray-200 dark:bg-gray-700 animate-pulse" />
              <div className="h-4 w-24 bg-gray-200 dark:bg-gray-700 rounded animate-pulse" />
            </div>
            <div className="h-3 w-20 bg-gray-200 dark:bg-gray-700 rounded animate-pulse mb-3" />
            <div className="h-4 w-48 bg-gray-200 dark:bg-gray-700 rounded animate-pulse" />
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <div className="flex items-center h-[50px] px-4 border-b border-border-gray dark:border-border-gray-dk bg-pure-white dark:bg-pure-white-dk">
        <h3 className="text-base font-semibold text-verba-indigo dark:text-verba-indigo-dk">
          {helpdeskLabels.ticketDetails}
        </h3>
      </div>
      <div className="flex-1 p-4 overflow-y-auto">
        <div className="border border-border-gray dark:border-border-gray-dk rounded-lg p-4 flex flex-col gap-3">
          <div className="flex items-center gap-2.5">
            <Avatar text={name || "U"} size={28} className="text-xs" />
            <span className="text-md font-semibold text-verba-indigo dark:text-verba-indigo-dk">
              {name || "Unknown"}
            </span>
          </div>

          <div className="flex items-center">
            <span className="text-sm font-medium text-verba-indigo dark:text-verba-indigo-dk px-2 py-0.5 border border-amber-300 dark:border-amber-400 bg-amber-50 dark:bg-amber-900/20 rounded">
              {helpdeskLabels.ticketSingular} Creator
            </span>
          </div>

          {email && (
            <Tooltip
              content="Email Address. Click to Copy"
              side="bottom"
              contentClassName="text-xs"
            >
              <div
                className="flex items-center gap-1.5 cursor-pointer w-max"
                onClick={handleCopyEmail}
              >
                <EmailIcon className="w-4 h-4 text-gray-placeholder dark:text-gray-placeholder-dk shrink-0" />
                <span className="text-sm font-normal text-heading-text dark:text-heading-text-dk hover:underline">
                  {email}
                </span>
              </div>
            </Tooltip>
          )}
        </div>
      </div>
    </>
  );
};

export default TicketDetailsContent;
